'use client';

/**
 * Keeps an already-permitted device attached to every order the guest has.
 *
 * The permission question is one-time , a browser holds one push subscription
 * per origin, and once it is granted nothing should ever ask again. But a guest
 * with no account can only be identified by the orders they hold, so the stored
 * rows are per order. Left alone, that means allowing notifications on one
 * receipt and then hearing nothing about the next order, which looks exactly
 * like the feature being broken.
 *
 * This component closes that gap. It renders nothing, never prompts, and never
 * asks for permission , it only acts when permission is *already* granted. Drop
 * it on any surface that knows the guest's orders (the menu pages, the business
 * page, the customer dashboard) and the device stays attached to all of them.
 *
 * Work is done once per device per order: a synced-id ledger in localStorage
 * keeps a returning guest from re-posting the same list on every page view.
 */

import { useEffect, useRef } from 'react';

import { listGuestOrders } from '@/lib/customer/guest-orders';

const SW_PATH = '/sw.js';
const SYNCED_KEY = 'mnrq:push-synced:v1';
/** Cap matches the bulk endpoint's, which matches guest-orders' own MAX_RECORDS. */
const MAX_BATCH = 20;

function readSynced(endpoint: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(SYNCED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as { endpoint?: string; ids?: string[] };
    // A new endpoint is a new device as far as the server is concerned, so the
    // ledger from the previous one says nothing about it.
    if (parsed?.endpoint !== endpoint || !Array.isArray(parsed.ids)) return new Set();
    return new Set(parsed.ids);
  } catch {
    return new Set();
  }
}

function writeSynced(endpoint: string, ids: Set<string>): void {
  try {
    window.localStorage.setItem(
      SYNCED_KEY,
      JSON.stringify({ endpoint, ids: [...ids].slice(-100) }),
    );
  } catch {
    /* storage disabled , we just re-post next time, which is harmless */
  }
}

export function PushOrderSync() {
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    void (async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
          return;
        }
        // Never prompt from here. Silence is the correct behaviour for a guest
        // who has not been asked yet, or who said no.
        if (Notification.permission !== 'granted') return;

        const orders = listGuestOrders();
        if (!orders.length) return;

        const registration =
          (await navigator.serviceWorker.getRegistration('/')) ??
          (await navigator.serviceWorker.register(SW_PATH, { scope: '/' }));

        const subscription = await registration.pushManager.getSubscription();
        // No subscription despite permission: the receipt's opt-in card owns
        // creating one. Minting a second here would race it into a duplicate.
        if (!subscription) return;

        const synced = readSynced(subscription.endpoint);
        const pending = orders.map((o) => o.id).filter((id) => !synced.has(id)).slice(0, MAX_BATCH);
        if (!pending.length) return;

        const json = subscription.toJSON();
        const p256dh = json.keys?.p256dh;
        const auth = json.keys?.auth;
        if (!p256dh || !auth) return;

        const res = await fetch('/api/public/push-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: { p256dh, auth },
            userAgent: navigator.userAgent,
            orderIds: pending,
          }),
        });
        if (!res.ok) return;

        pending.forEach((id) => synced.add(id));
        writeSynced(subscription.endpoint, synced);
      } catch (err) {
        // Best-effort background work: it must never surface in an ordering flow.
        console.warn('[push] order sync skipped', err);
      }
    })();
  }, []);

  return null;
}
